import {
  BaseQueryApi,
  BaseQueryFn,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootStateOrAny } from 'react-redux';

import { getNewDateFunction, isTimeTravelActive } from '../../../redux-core';

import { BaseQueryArg, FetchBaseQueryArgs, QueryReturnValue } from './types';

type FetchBentoQuery = (
  args?: Partial<FetchBaseQueryArgs>
) => ReturnType<typeof fetchBaseQuery>;
export const fetchBentoQuery: FetchBentoQuery = (options) => {
  return fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, thunkAPI) => {
      const state = thunkAPI.getState() as RootStateOrAny;
      if (state.borderfree?.isBorderfreeCustomer) {
        headers.set('x-tfg-borderfree', 'true');
      }
      if (isTimeTravelActive(state)) {
        // @ts-ignore
        const newDateFn = getNewDateFunction(state);
        headers.set('x-tfg-effective-date', newDateFn().toISOString());
      }
      if (state.session.isAdmin) {
        headers.set('x-tfg-content-preview', 'true');
      }
      if (
        options?.prepareHeaders &&
        typeof options?.prepareHeaders === 'function'
      ) {
        return options.prepareHeaders(headers, thunkAPI);
      }
      return headers;
    },
  });
};

export type BentoAPI = typeof global.fetch;

interface ThunkExtraArg {
  bentoApi: BentoAPI;
}

type ApiContext = Required<BaseQueryApi> & {
  extra: ThunkExtraArg;
};

type WithBentoApiFn<TArgs, TResult> = (
  args: TArgs,
  bentoApi: BentoAPI,
  api: ApiContext,
  extraOptions?: {},
  baseQuery?: BaseQueryFn
) => Promise<
  QueryReturnValue<TResult, FetchBaseQueryError, FetchBaseQueryMeta>
>;

export function withBentoApi<TArgs, TResult>(
  queryFn: WithBentoApiFn<TArgs, TResult>
): (
  args: TArgs,
  api: ApiContext,
  extraOptions?: {},
  baseQuery?: BaseQueryArg<any>
) => Promise<
  QueryReturnValue<TResult, FetchBaseQueryError, FetchBaseQueryMeta>
> {
  return async (args, api, extraOptions, baseQuery) => {
    const { bentoApi } = api.extra as ThunkExtraArg;
    return queryFn(args, bentoApi, api, extraOptions, baseQuery);
  };
}

export const handleLegacyReduxActions = <TArg>([onStart, onSuccess, onError]: [
  string,
  string,
  string
]) => {
  return async (arg: TArg, { dispatch, queryFulfilled }) => {
    dispatch({ type: onStart, payload: arg });

    try {
      const { data, meta } = await queryFulfilled;
      dispatch({
        type: onSuccess,
        payload: data,
        meta: {
          ...meta,
          fetchedDate: new Date().toISOString(),
        },
      });
    } catch (error) {
      dispatch({ type: onError, payload: arg });
    }
  };
};
